import * as React from 'react';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { ReactComponent as SearchIcon } from 'common/dist/assets/search.svg';

import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';

import * as api from '../../api';
import * as logger from '../../logger';
import * as store from '../../reducers/store';
import * as rootReducer from '../../reducers/root';

import styles from './index.module.css';
import { AutocompleteAsyncInput, Suggestion } from '../AutocompleteAsyncInput';
import { ModalInput } from '../ModalInput';
import { PatientRouteParams } from '../../scenes/router';
import { getCoordinates } from '../../utils/location';
import { VerifyAddressContainer } from './VerifyAddressContainer';
import ConfirmAddress from './ConfirmAddress';

const MANUAL_ADDRESS_PLACE_ID = 'manual_place_id';
export const TRUNCATE_LENGTH = 30;
const EMPTY_STRING = '';

interface StateProps {
  selectedFormSet: store.FormSet;
  coordinates?: store.Coordinates;
}

interface DispatchProps {
  setCoordinates: (coordinates: api.Places.Geometry) => void;
}

interface Props
  extends WithTranslation,
    StateProps,
    DispatchProps,
    RouteComponentProps<PatientRouteParams> {
  id?: string;
  initialPlace?: api.Places.PlaceDetails;
  disabled?: boolean;
  placeholder?: string;
  onSave: (place: api.Places.PlaceDetails) => void;
  previsitAddressValidCharacterSet: api.PracticePreference.PrevisitAddressValidCharacterSet;
}

interface State {
  isModalOpen: boolean;
  fullAddress: string;
  selectedPlace?: api.Places.PlaceDetails;
  currentText: string;
}

class AddressInput extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { initialPlace, previsitAddressValidCharacterSet } = props;

    this.state = {
      isModalOpen: false,
      fullAddress: initialPlace
        ? placeToString(initialPlace, previsitAddressValidCharacterSet)
        : '',
      currentText: EMPTY_STRING,
    };
  }

  async componentDidMount() {
    const { authenticationCode } = this.props.match.params;
    try {
      const newCoords = this.props.coordinates ?? (await getCoordinates());
      if (newCoords) {
        await api.client.Previsit.createPrevisitLog({
          auditLog:
            api.Log.PrevisitAuditAction.PatientPrevisitAcceptedGeolocation,
          previsitLog: api.Log.PrevisitAction.AcceptedGeolocation,
          authenticationCode,
        });
        this.props.setCoordinates(newCoords);
      }
    } catch (err) {
      await api.client.Previsit.createPrevisitLog({
        auditLog:
          api.Log.PrevisitAuditAction.PatientPrevisitDeclinedGeolocation,
        previsitLog: api.Log.PrevisitAction.DeclinedGeolocation,
        authenticationCode,
      });
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    const { initialPlace, previsitAddressValidCharacterSet } = this.props;
    if (prevProps.initialPlace !== initialPlace) {
      this.setState({
        fullAddress: initialPlace
          ? placeToString(initialPlace, previsitAddressValidCharacterSet)
          : '',
        currentText: EMPTY_STRING,
      });
    }
  }

  setIsModalOpen = (newState: boolean) => {
    this.setState({ isModalOpen: newState });
  };

  selectPlace = async (selectedPlace: api.Places.PlaceSuggestion) => {
    if (selectedPlace.placeId === MANUAL_ADDRESS_PLACE_ID) {
      const regex = new RegExp(`${this.props.t('add')} `);
      const addressOne = selectedPlace.mainText
        .replace(regex, '')
        .replace(/"/g, '');
      const abbreviatedAddressOne = applyAbbreviationsAndTruncate(addressOne);

      this.setState({
        selectedPlace: {
          addressOne: abbreviatedAddressOne,
          addressTwo: '',
          city: '',
          state: '',
          zipCode: '',
          longitude: null,
          latitude: null,
        },
      });
      return;
    }

    try {
      const selectedPlaceDetails = await api.client.Places.findPlaceDetails(
        selectedPlace.placeId,
      );

      this.setState({
        selectedPlace: {
          ...selectedPlaceDetails,
          addressOne: applyAbbreviationsAndTruncate(
            selectedPlaceDetails.addressOne,
          ),
          addressTwo: applyAbbreviationsAndTruncate(
            selectedPlaceDetails.addressTwo,
          ),
          city: selectedPlaceDetails.city.toUpperCase(),
        },
      });
    } catch (err) {
      logger.error(err, '.selectPlace Failed finding place details');
    }
  };

  saveAddress = (address: api.Places.PlaceDetails) => {
    const { onSave, previsitAddressValidCharacterSet } = this.props;

    const regex =
      api.Previsit.addressValidCharacterSetMap[
        previsitAddressValidCharacterSet
      ];
    if (regex) {
      const replacedAddress = Object.keys(address).reduce(
        (acc, curr) => {
          const value = address[curr as keyof api.Places.PlaceDetails];
          return {
            ...acc,
            [curr]:
              (typeof value === 'string' && regexReplace(value, regex, '')) ||
              value,
          };
        },
        {
          addressOne: '',
          addressTwo: '',
          city: '',
          state: '',
          zipCode: '',
          longitude: null,
          latitude: null,
        },
      );
      onSave(replacedAddress);
    } else {
      onSave(address);
    }

    this.setState({
      fullAddress: placeToString(address, previsitAddressValidCharacterSet),
      selectedPlace: undefined,
    });
    this.setIsModalOpen(false);
  };

  handleSelect = (suggestion: Suggestion<api.Places.PlaceSuggestion>) => {
    const { data } = suggestion;

    if (!data) return;

    this.selectPlace(data);
  };

  getPlaceSuggestions = async (
    input: string,
  ): Promise<Suggestion<api.Places.PlaceSuggestion>[]> => {
    const { coordinates, selectedFormSet, t } = this.props;
    const trimmedInput = input.trim();

    if (!trimmedInput) return [];

    const suggestions = await api.client.Places.findAutocompleteSuggestions({
      input: trimmedInput,
      practiceId: selectedFormSet.practice?.id,
      latitude: coordinates?.latitude,
      longitude: coordinates?.longitude,
    });

    const placeSuggestions = suggestions.map((places) => {
      return {
        primaryLabel: places.mainText,
        secondaryLabel: places.secondaryText,
        icon: <LocationOnIcon />,
        data: places,
      };
    });

    const manualPlaceSuggestion = {
      mainText: t('addInput', {
        input: `"${trimmedInput}"`,
      }),
      secondaryText: '',
      placeId: MANUAL_ADDRESS_PLACE_ID,
    };

    return [
      ...placeSuggestions.slice(0, 4),
      {
        primaryLabel: manualPlaceSuggestion.mainText,
        secondaryLabel: manualPlaceSuggestion.secondaryText,
        icon: <LocationOnIcon />,
        data: manualPlaceSuggestion,
      },
    ];
  };

  renderChildren = () => {
    const { selectedPlace, currentText } = this.state;
    const { selectedFormSet, t } = this.props;
    const shouldVerifyPractice = [
      '504e4740-e46e-4a8d-abc5-b0ef39a1e756', // Prod Krieger Medical Practice for testing
      'e92d8d29-6fdd-41cf-a6c9-2744908a5456', // Sanford Health - Test SA 10
      '43d08f4a-78d0-4f1e-b633-bd2dd5a02944', // Sanford health - test - SA 15
      '325be2e6-92cd-4cc2-8c00-e13f973588a9', // Austin Regional Clinic - Test
      '9a159d0b-9874-403d-ad58-3380e87fd642', // Austin Regional Clinic - Northwest Hills Spec - Infusion
    ].includes(selectedFormSet.practice?.id || '');
    const shouldVerifyPracticeGroup = [
      '4a26597d-7910-4de0-9606-9de843f27032', // ARC
      '6415f058-ab85-401d-9206-223ee93a8bf0', // ARC - Coverage Automation
      'b82a96ae-9727-4001-8789-b98978fdc728', // Sanford Health
    ].includes(selectedFormSet.practice?.practiceGroupId || '');
    const shouldVerifyWithServer =
      shouldVerifyPractice || shouldVerifyPracticeGroup;

    if (selectedPlace) {
      return shouldVerifyWithServer ? (
        <VerifyAddressContainer
          address={selectedPlace}
          onSave={this.saveAddress}
        />
      ) : (
        <ConfirmAddress place={selectedPlace} onSave={this.saveAddress} />
      );
    }

    return (
      <AutocompleteAsyncInput
        onSelect={this.handleSelect}
        getSuggestions={this.getPlaceSuggestions}
        placeholder={`${t('enterAddress')}...`}
        icon={<SearchIcon className={styles.searchIcon} />}
        limit={5}
        initialString={currentText}
      />
    );
  };

  closeModal = () => {
    this.setState({
      isModalOpen: false,
      selectedPlace: undefined,
      currentText: EMPTY_STRING,
    });
  };

  render() {
    const { id, disabled = false, placeholder, t } = this.props;
    const { fullAddress, isModalOpen } = this.state;

    return (
      <ModalInput
        id={id}
        children={this.renderChildren()}
        initialValue={fullAddress}
        isOpen={isModalOpen}
        setIsOpen={this.setIsModalOpen}
        setCurrentText={(currentText) => this.setState({ currentText })}
        placeholder={placeholder || t('placeholderAddress')}
        onModalClose={this.closeModal}
        disabled={disabled}
        modalTitle={t('enterAddress')}
        header={true}
      />
    );
  }
}

function regexReplace(source: string, regex: string, replaceWith: string) {
  return source.replace(new RegExp(regex, 'gm'), replaceWith);
}

export function placeToString(
  place: api.Places.PlaceDetails,
  previsitAddressValidCharacterSet: api.PracticePreference.PrevisitAddressValidCharacterSet,
): string {
  const { addressOne, addressTwo, city, state, zipCode } = place;

  const regex =
    api.Previsit.addressValidCharacterSetMap[previsitAddressValidCharacterSet];
  if (regex) {
    return [addressOne, addressTwo, city, state, zipCode]
      .filter((x) => !!(x && x.trim()))
      .map((x) => regexReplace(x, regex, ''))
      .join(', ');
  }

  return [addressOne, addressTwo, city, state, zipCode]
    .filter((x) => !!(x && x.trim()))
    .join(', ');
}

export function applyAbbreviationsAndTruncate(
  input: string,
  truncateLength: number = TRUNCATE_LENGTH,
) {
  return applyAbbreviations(input).slice(0, truncateLength);
}

function applyAbbreviations(input: string) {
  return input
    .toUpperCase()
    .split(' ')
    .map(
      (word) =>
        api.PrevisitAddress.AddressAbbreviations.ADDRESS_ABBREVIATIONS_MAPPING[
          word
        ] || word,
    )
    .join(' ');
}

function mapStateToProps({
  coordinates,
  formSets,
  selectedFormSetId,
}: store.FormSetSelected & store.Coordinates): StateProps {
  const selectedFormSet = formSets[selectedFormSetId];
  return { selectedFormSet, coordinates };
}

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setCoordinates: (coordinates: api.Places.Geometry) =>
      dispatch(rootReducer.setCoordinates(coordinates)),
  };
}

const AddressInputRouter = withRouter(AddressInput);
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(AddressInputRouter));
